/**
 * Publisher filtering utility. Google is gated solely by the
 * ENABLE_GOOGLE_DEMANDGEN feature flag, which is set per stage in
 * PVMMELeelaServiceCDK/lib/constants.ts (FEATURE_FLAG_DEFAULTS).
 *
 * No LDAP/Brass check is involved: integ-test identities aren't members of any
 * Amazon LDAP group, so a membership gate makes the option unreachable in tests.
 */
import { CampaignPublishers } from 'src/store/constants';
import { FEATURE_FLAGS } from 'src/constants';
import { isFeatureEnabled } from 'src/feature-flags';
import type { CampaignDesiredStateDto } from 'src/store/generated/leela-api-generated';

/**
 * Returns the list of publishers available to the current user.
 * Google appears only when ENABLE_GOOGLE_DEMANDGEN is on for the stage.
 * Fails closed: if the flag cache isn't populated yet, google is excluded.
 */
export const getAvailablePublishers = (): Array<CampaignDesiredStateDto['publisher']> =>
  Object.values(CampaignPublishers).filter(
    p => p !== CampaignPublishers.GOOGLE || isFeatureEnabled(FEATURE_FLAGS.ENABLE_GOOGLE_DEMANDGEN),
  );

/**
 * Derived flag the backend exposes per publisher listed in `enableExecutionLayer.publishers`
 * (GET /feature-flags), e.g. `enableExecutionLayer.publishers.adsp`. It is the same key the backend's
 * routing gate reads, so the UI turns on exactly where the execution layer processes the publisher.
 */
export const executionLayerPublisherFlag = (publisher: CampaignDesiredStateDto['publisher']): string =>
  `${FEATURE_FLAGS.ENABLE_EXECUTION_LAYER}.publishers.${publisher}`;

/**
 * Whether a campaign's publisher gets the execution-layer ExecutionWorkflows view instead of the
 * legacy program workflows. Google always qualifies (unchanged). Every other publisher qualifies when
 * the backend routes it through the execution layer, i.e. `enableExecutionLayer.publishers.<publisher>`
 * is on. Fails closed: before flags load, or for an unknown publisher, it returns false.
 */
export const isExecutionLayerUiEnabled = (publisher: CampaignDesiredStateDto['publisher'] | undefined): boolean => {
  if (!publisher) return false;
  if (publisher === CampaignPublishers.GOOGLE) return true;
  return isFeatureEnabled(executionLayerPublisherFlag(publisher));
};
